const audiences = [
  {
    icon: 'ri-user-heart-line',
    emoji: '🤰',
    title: 'Expecting Mothers',
    subtitle: 'Track pregnancy safely',
    description: 'Get week-by-week guidance, hospital recommendations, and preparation checklists for a safe and confident pregnancy journey.',
    features: ['Weekly pregnancy updates', 'Hospital comparison tool', 'Trimester care checklist', 'Nutrition & health tips'],
    color: 'rose',
    image: 'https://public.readdy.ai/ai/img_res/9b59694ef49ade0c4e516637efe76a4d.jpg'
  },
  {
    icon: 'ri-parent-line',
    emoji: '👶',
    title: 'New Parents',
    subtitle: 'Manage baby care & vaccines',
    description: 'Track your newborn\'s vaccine schedule, health milestones, and growth with smart reminders that ensure you never miss a critical appointment.',
    features: ['Vaccine schedule tracker', 'Growth milestone alerts', 'Smart health reminders', 'Baby development guide'],
    color: 'teal',
    image: 'https://readdy.ai/api/search-image?query=happy%20young%20indian%20mother%20and%20father%20sitting%20together%20on%20sofa%20smiling%20lovingly%20holding%20cute%20newborn%20baby%20wrapped%20in%20white%20blanket%20bright%20airy%20living%20room%20soft%20natural%20light%20warm%20tones%20joyful%20new%20parents%20portrait%20professional%20photography&width=500&height=400&seq=new-parents-clean-v3&orientation=landscape'
  },
  {
    icon: 'ri-team-line',
    emoji: '👨‍👩‍👧',
    title: 'Families',
    subtitle: 'Make informed healthcare decisions',
    description: 'Support your loved ones with clear, data-driven information about the best hospitals and care options available in Pune.',
    features: ['Hospital cost comparison', 'Location-based search', 'NICU availability info', 'Emergency care details'],
    color: 'sky',
    image: 'https://readdy.ai/api/search-image?query=happy%20indian%20family%20three%20generations%20grandmother%20grandfather%20parents%20and%20toddler%20child%20sitting%20together%20in%20bright%20living%20room%20smiling%20warmly%20soft%20white%20background%20natural%20light%20cheerful%20family%20portrait%20professional%20photography%20clean%20minimal&width=500&height=400&seq=family-clean-v3&orientation=landscape'
  }
];

const borderColors: Record<string, string> = {
  rose: 'border-rose-100 hover:border-rose-200',
  teal: 'border-teal-100 hover:border-teal-200',
  sky: 'border-sky-100 hover:border-sky-200'
};

const iconColors: Record<string, string> = {
  rose: 'bg-rose-50 text-rose-500',
  teal: 'bg-teal-50 text-teal-600',
  sky: 'bg-sky-50 text-sky-600'
};

const badgeColors: Record<string, string> = {
  rose: 'bg-rose-50 text-rose-600 border-rose-100',
  teal: 'bg-teal-50 text-teal-700 border-teal-100',
  sky: 'bg-sky-50 text-sky-700 border-sky-100'
};

const dotColors: Record<string, string> = {
  rose: 'bg-rose-400',
  teal: 'bg-teal-500',
  sky: 'bg-sky-500'
};

export default function WhoIsItFor() {
  return (
    <section id="who-its-for" className="py-20 sm:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 border border-sky-100 rounded-full mb-5">
            <i className="ri-team-line text-sky-600"></i>
            <span className="text-sm font-semibold text-sky-700">Who It's For</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
            Who Can Use{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-sky-500">MomsFirstCare</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Whether you're pregnant, a new parent, or supporting a family member MomsFirstCare is built for you.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {audiences.map((audience, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl border ${borderColors[audience.color]} overflow-hidden hover:-translate-y-2 transition-all`}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={audience.image}
                  alt={audience.title}
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <div className={`absolute top-4 left-4 border text-xs font-bold px-3 py-1 rounded-full bg-white/90`}>
                  <span className="mr-1">{audience.emoji}</span>{audience.subtitle}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${iconColors[audience.color]}`}>
                    <i className={`${audience.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{audience.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{audience.description}</p>
                <ul className="space-y-1.5">
                  {audience.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-500">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColors[audience.color]} shrink-0`}></span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
